import { Component, OnInit, AfterViewInit, AfterViewChecked, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { pointInPixelPoly } from '../../route-builder-v2/utils/lasso-geometry';

declare const google: any;

@Component({
	selector: 'app-beautician-prospecting',
	templateUrl: './beautician-prospecting.component.html',
	styleUrls: ['./beautician-prospecting.component.scss']
})
export class BeauticianProspectingComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
	@ViewChild('mapHost', { static: false }) mapHost: ElementRef<HTMLDivElement>;
	@ViewChild('lassoCanvas', { static: false }) lassoCanvas: ElementRef<SVGSVGElement>;

	color = 'blue';
	config: any = [];
	formGroup: UntypedFormGroup;
	loading = false;

	dataSource = new MatTableDataSource<any>([]);
	mapData: any[] = [];
	byrep: any = {};
	byrepKeys: string[] = [];
	count = 0;
	pageIndex = 0;
	pageSize = 100;
	sortField = '';
	sortDir: 'asc' | 'desc' = 'asc';
	private dataVersion = 0;
	mode = 'prospects';
	repOptions: string[] = [];
	ran = false;
	excellink = '';
	pdflink = '';
	mapStatus = '';
	mapCount = 0;
	scratchAssigned = 0;
	scratchConverted = 0;
	scratchEnabled = true;
	assignRep = '';
	selectedKeys = new Set<string>();
	activeRowKey = '';
	drawHint = '';
	mapTypeId = 'roadmap';
	lassoMode: 'none' | 'add' | 'remove' = 'none';
	private filterSub: Subscription;
	private map: any = null;
	// markerByKey: reused single-prospect pins (updated in place across
	// repaints, never destroyed-and-rebuilt); clusterMarkers: count bubbles,
	// cheap to rebuild. Reuse is what keeps selection/pan repaints smooth.
	private clusterMarkers: any[] = [];
	private markerByKey = new Map<string, any>();
	private infoWindow: any = null;
	private infoPinned = false;
	private lastMapSignature = '';
	private hasAutoFit = false;
	private idleListener: any = null;
	private mapListeners: any[] = [];
	private resizeObs: ResizeObserver | null = null;
	private paintFrame: number | null = null;
	private mapHostEl: HTMLElement | null = null;
	private mapPaintPending = false;
	private fetchGen = 0;
	private readonly clusterThreshold = 300;
	private readonly clusterCellPx = 56;

	// Freehand lasso (ported from RB2 planner map): a pixel-space polygon the
	// user draws over the map; we hit-test each plotted prospect through the
	// map projection. More reliable than google.maps.drawing (no extra lib,
	// works while clustered) and matches the RB2 selection UX.
	private lassoActive = false;
	private lassoRemove = false;
	private lassoDrawing = false;
	private lassoPts: Array<{ x: number; y: number }> = [];
	private lassoPointerId: number | null = null;
	private lassoSavedGest: any = null;
	private readonly lassoDown = (e: PointerEvent) => this.onLassoDown(e);
	private readonly lassoMove = (e: PointerEvent) => this.onLassoMove(e);
	private readonly lassoUp = (e: PointerEvent) => this.onLassoUp(e);
	private readonly blockTouch = (e: TouchEvent) => e.preventDefault();

	prospectCols: string[] = ['convert', 'possible_dup', 'rep', 'assignment_rep', 'business_name', 'est_potential', 'est_twin', 'owner', 'address', 'city', 'zip5', 'license_number', 'license_type', 'ops_at_addr', 'license_issue_date', 'license_expiration_date'];
	matchedCols: string[] = ['matched_rep', 'assignment_rep', 'business_name', 'address', 'city', 'zip5', 'license_number', 'matched_debtorno', 'matched_name', 'last_order', 'ttm_sales'];

	ltypeOptions = [
		{ label: 'All license types', value: '' },
		{ label: 'Salon / business', value: 'DOSAEBUSINESS' },
		{ label: 'Booth / area renter', value: 'DOSAERENTER' },
		{ label: 'Barber shop', value: 'DOSBARSHOPOWNER' },
	];

	constructor(
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private fb: UntypedFormBuilder,
		private router: Router
	) {}

	// Convert a prospect into a new customer: hand the license row to the
	// new-customer form (customers/create), which prefills name/address/license/
	// owner so the user only confirms terms/salesperson/tax before saving.
	convertProspect(row: any, event?: Event): void {
		if (event) { event.stopPropagation(); }
		if (!row) { return; }
		this.router.navigate(['/customers/create'], {
			state: {
				salonProspect: {
					name: row.business_name || '',
					address1: row.address || '',
					address2: row.suite || '',
					city: row.city || '',
					state: 'NY',
					zip: row.zip5 || '',
					license_number: row.license_number || '',
					license_expiration_date: row.license_expiration_date || '',
					owner: row.owner || '',
					prospect_key: row.prospect_key || '',
				},
			},
		});
	}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.configsubscription.subscribe(r => { this.config = r; });
		this.formGroup = this.fb.group({
			mode: ['prospects'],
			ltype: [''],
			reps: [[]],
			min_ops: [0],
		});
		this.filterSub = this.formGroup.valueChanges.pipe(
			debounceTime(350),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
		).subscribe(() => { this.pageIndex = 0; this.runReport(); });
		const savedType = localStorage.getItem('aa_bp_map_type');
		if (savedType === 'roadmap' || savedType === 'hybrid') {
			this.mapTypeId = savedType;
		}
		this.runReport();
	}

	ngAfterViewInit(): void {
		// Table is server-paginated/sorted (page + sort events refetch), so we
		// intentionally do NOT wire dataSource.paginator/sort — that would
		// re-slice/sort only the current page on the client.
		this.schedulePaint();
	}

	ngAfterViewChecked(): void {
		if (!this.mapPaintPending || !this.mapData.length || !this.mapHost?.nativeElement) {
			return;
		}
		this.mapPaintPending = false;
		this.schedulePaint(true);
	}

	onPage(e: { pageIndex: number; pageSize: number }): void {
		this.pageIndex = e.pageIndex;
		this.pageSize = e.pageSize;
		this.runReport(true);
	}

	onSortChange(e: { active: string; direction: 'asc' | 'desc' | '' }): void {
		this.sortField = e.direction ? e.active : '';
		this.sortDir = e.direction || 'asc';
		this.pageIndex = 0;
		this.runReport(true);
	}

	get isProspect(): boolean { return this.mode === 'prospects'; }
	get displayedColumns(): string[] { return this.isProspect ? this.prospectCols : this.matchedCols; }
	get countLabel(): string {
		if (this.mode === 'conversions') {
			return 'conversions';
		}
		return this.isProspect ? 'unmatched businesses' : 'matched customers';
	}

	ngOnDestroy(): void {
		if (this.filterSub) {
			this.filterSub.unsubscribe();
		}
		if (this.paintFrame != null) {
			cancelAnimationFrame(this.paintFrame);
			this.paintFrame = null;
		}
		this.exitLasso();
		this.destroyMap();
	}

	// tableOnly = a page/sort refetch: only the table page changes, so the
	// server skips the map/counts/rep-list and we leave the map untouched.
	runReport(tableOnly = false): void {
		const fetchGen = ++this.fetchGen;
		this.loading = true;
		const payload: any = {
			...this.formGroup.value,
			page: this.pageIndex,
			page_size: this.pageSize,
			sort: this.sortField,
			dir: this.sortDir,
		};
		if (tableOnly) { payload.no_map = 1; }
		this.excellink = this.generalLedgerService.getBeauticianProspectsExportLink(this.formGroup.value, 'xls');
		this.pdflink = this.generalLedgerService.getBeauticianProspectsExportLink(this.formGroup.value, 'pdf');
		this.generalLedgerService.getBeauticianProspects(payload).subscribe((res: any) => {
			if (fetchGen !== this.fetchGen) { return; }
			this.loading = false;
			this.ran = true;
			if (!res) { return; }
			this.dataSource.data = res.data || [];
			if (tableOnly) { return; }
			this.mode = res.mode;
			this.excellink = res.excellink || this.excellink;
			this.pdflink = res.pdflink || this.pdflink;
			this.mapData = res.map || [];
			this.byrep = res.byrep || {};
			this.byrepKeys = Object.keys(this.byrep);
			this.count = res.count || 0;
			this.scratchAssigned = res.scratch?.assigned_count || 0;
			this.scratchConverted = res.scratch?.converted_count || 0;
			this.scratchEnabled = res.scratch_enabled !== false;
			if (!this.scratchEnabled) {
				this.mapStatus = 'Scratch assignments need the migration';
			}
			this.repOptions = res.rep_options || this.byrepKeys;
			const selected = this.formGroup.get('reps')?.value || [];
			const validSelected = selected.filter((rep: string) => this.repOptions.includes(rep));
			if (validSelected.length !== selected.length) {
				this.formGroup.get('reps')?.setValue(validSelected, { emitEvent: false });
			}
			this.dataVersion++;
			this.lastMapSignature = '';
			this.hasAutoFit = false;
			this.schedulePaint(true);
		});
	}

	ltypeLabel(t: string): string {
		return (t || '').replace('DOSAE', 'AE-').replace('DOSBAR', 'BAR-');
	}

	private schedulePaint(force = false): void {
		if (force && this.paintFrame != null) {
			cancelAnimationFrame(this.paintFrame);
			this.paintFrame = null;
		}
		if (this.paintFrame != null) { return; }
		this.paintFrame = requestAnimationFrame(() => {
			this.paintFrame = null;
			if (!this.paintMap()) {
				this.mapPaintPending = true;
			}
		});
	}

	private mapStyles(): any[] {
		return [
			{ elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
			{ elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
			{ elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
			{ elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
			{ featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#d1d5db' }] },
			{ featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#374151' }] },
			{ featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
			{ featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
			{ featureType: 'poi', stylers: [{ visibility: 'off' }] },
			{ featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5f0e0' }] },
			{ featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
			{ featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
			{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#f3f4f6' }] },
			{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#fef3c7' }] },
			{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#fcd34d' }] },
			{ featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#fcd34d' }] },
			{ featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
			{ featureType: 'transit', stylers: [{ visibility: 'off' }] },
			{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
			{ featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3b82f6' }] },
		];
	}

	private paintMap(): boolean {
		const host = this.mapHost?.nativeElement;
		if (!host || !this.mapData.length) {
			if (!this.mapData.length) {
				this.destroyMap();
			}
			return false;
		}
		if (typeof google === 'undefined' || !google.maps) {
			this.mapStatus = 'Map unavailable';
			return true;
		}
		if (this.map && this.mapHostEl !== host) {
			this.destroyMap();
		}
		this.mapHostEl = host;
		if (!this.map) {
			this.map = new google.maps.Map(host, {
				center: { lat: 42.92, lng: -78.82 },
				zoom: 8,
				mapTypeId: this.mapTypeId,
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: true,
				clickableIcons: false,
				gestureHandling: 'greedy',
				backgroundColor: '#eef2f5',
				styles: this.mapTypeId === 'roadmap' ? this.mapStyles() : [],
			});
			this.infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
			this.infoWindow.addListener('closeclick', () => { this.infoPinned = false; });
			// Clicking empty map unpins/closes the detail bubble.
			this.mapListeners.push(google.maps.event.addListener(this.map, 'click', () => {
				this.infoPinned = false;
				this.infoWindow.close();
			}));
			this.idleListener = google.maps.event.addListener(this.map, 'maptypeid_changed', () => {
				const type = this.map.getMapTypeId();
				this.mapTypeId = type === 'roadmap' ? 'roadmap' : 'hybrid';
				localStorage.setItem('aa_bp_map_type', this.mapTypeId);
				this.map.setOptions({ styles: type === 'roadmap' ? this.mapStyles() : [] });
			});
			this.mapListeners.push(this.idleListener);
			// Re-cluster/repaint when the camera settles so bubbles stay crisp
			// as you pan and zoom (RB2 does the same on idle).
			this.mapListeners.push(google.maps.event.addListener(this.map, 'idle', () => this.schedulePaint()));
			// Keep the canvas sized to its container (RB2 pattern) — Google
			// otherwise renders grey tiles when the panel resizes.
			if (typeof ResizeObserver !== 'undefined') {
				this.resizeObs = new ResizeObserver(() => {
					if (this.map) { google.maps.event.trigger(this.map, 'resize'); }
				});
				this.resizeObs.observe(host);
			}
			setTimeout(() => {
				if (this.map) {
					google.maps.event.trigger(this.map, 'resize');
					this.paintMap();
				}
			}, 350);
			google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
				if (this.map && this.mapData.length) {
					this.fitMapToRows(this.mapData.filter(r => this.validCoord(r.lat, r.lng)));
					// Projection is ready now — repaint so a first pass that
					// bailed for want of it actually draws the clusters.
					this.schedulePaint();
				}
			});
		}

		// Don't churn markers behind the lasso SVG while a drag is in flight.
		if (this.lassoActive && this.lassoDrawing) { return true; }

		const rows = this.mapData.filter(r => this.validCoord(r.lat, r.lng));
		this.mapCount = rows.length;
		if (!rows.length) {
			this.clearMapMarkers();
			this.mapStatus = 'No mapped rows';
			return true;
		}

		// Clustering needs the map's world→pixel projection. On the very first
		// paint (map just created) it isn't ready yet — bail and let the map's
		// own `idle`/tilesloaded fire another paint once it is. Without this we
		// used to fall through and plot all ~7,900 pins individually, which
		// froze the map to a blank pane.
		const wantCluster = rows.length > this.clusterThreshold;
		if (wantCluster && !this.map.getProjection()) {
			this.lastMapSignature = '';   // force a real paint on the retry
			return false;
		}

		// Cheap signature: the row SET only changes on a full reload (dataVersion),
		// so we avoid rebuilding a 7,900-key string every idle. Selection is small.
		const signature = this.dataVersion
			+ '::' + rows.length
			+ '::' + Array.from(this.selectedKeys).sort().join(',')
			+ '::' + (this.map.getZoom() || 8);
		if (signature === this.lastMapSignature) {
			this.mapStatus = this.scratchEnabled ? '' : 'Scratch assignments need the migration';
			return true;
		}
		this.lastMapSignature = signature;

		if (wantCluster) {
			this.clearMapMarkers();
			this.paintMapClusters(rows);
			this.fitMapToRows(rows);
		} else {
			this.clearClusterMarkers();
			this.paintIndividualMarkers(rows);
			const bounds = new google.maps.LatLngBounds();
			rows.forEach(r => bounds.extend({ lat: Number(r.lat), lng: Number(r.lng) }));
			this.fitMapToRows(rows, bounds);
		}
		this.mapStatus = this.scratchEnabled ? '' : 'Scratch assignments need the migration';
		return true;
	}

	private destroyMap(): void {
		this.clearMapMarkers();
		if (this.map) {
			if (typeof google !== 'undefined' && google.maps) {
				this.mapListeners.forEach(l => google.maps.event.removeListener(l));
			}
			this.mapListeners = [];
			this.idleListener = null;
			this.map = null;
		}
		if (this.resizeObs) {
			this.resizeObs.disconnect();
			this.resizeObs = null;
		}
		this.mapHostEl = null;
		this.lastMapSignature = '';
		this.hasAutoFit = false;
	}

	// Reuse existing pins across repaints: update the ones still present,
	// create the new ones, drop the gone ones. Avoids the destroy-and-rebuild
	// churn that made selection/pan feel janky.
	private paintIndividualMarkers(rows: any[]): void {
		const seen = new Set<string>();
		for (const r of rows) {
			const key = this.markerKey(r);
			seen.add(key);
			let marker = this.markerByKey.get(key);
			if (!marker) {
				marker = new google.maps.Marker({
					position: { lat: Number(r.lat), lng: Number(r.lng) },
					map: this.map,
					title: r.business_name || r.address || '',
					optimized: true,
					icon: this.markerIcon(r),
				});
				marker.set('bpRow', r);
				this.bindMarkerInfo(marker);
				this.markerByKey.set(key, marker);
			} else {
				marker.set('bpRow', r);
				marker.setIcon(this.markerIcon(r));
			}
		}
		this.markerByKey.forEach((m, key) => {
			if (!seen.has(key)) {
				m.setMap(null);
				this.markerByKey.delete(key);
			}
		});
	}

	private markerIcon(r: any): any {
		return {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: this.markerColor(r),
			fillOpacity: 0.82,
			strokeColor: '#ffffff',
			strokeWeight: 1,
			scale: this.pinScale(r),
		};
	}

	// Size prospect pins by estimated potential so the bigger opportunities
	// read at a glance; matched customers stay a fixed size.
	private pinScale(r: any): number {
		if (!this.isProspect) { return 5; }
		const est = Number(r.est_potential) || 0;
		if (est >= 4000) { return 8; }
		if (est >= 2500) { return 6.5; }
		if (est >= 1500) { return 5; }
		return 3.5;
	}

	private clearClusterMarkers(): void {
		this.clusterMarkers.forEach(marker => marker.setMap(null));
		this.clusterMarkers = [];
	}

	private clearMapMarkers(): void {
		this.markerByKey.forEach(marker => marker.setMap(null));
		this.markerByKey.clear();
		this.clearClusterMarkers();
	}

	startLasso(remove: boolean): void {
		if (!this.scratchEnabled) {
			this.mapStatus = 'Scratch assignments need the migration';
			return;
		}
		if (!this.map || !this.lassoCanvas?.nativeElement) {
			this.mapStatus = 'Lasso unavailable';
			return;
		}
		this.enterLasso(remove);
	}

	setMapType(type: 'roadmap' | 'hybrid'): void {
		if (this.map) { this.map.setMapTypeId(type); }
	}

	private enterLasso(remove: boolean): void {
		this.exitLasso();
		this.lassoActive = true;
		this.lassoRemove = remove;
		this.lassoMode = remove ? 'remove' : 'add';
		const canvas = this.lassoCanvas.nativeElement;
		canvas.removeAttribute('hidden');
		canvas.classList.add('is-active');
		// Freeze the map so the drag paints a lasso instead of panning.
		this.lassoSavedGest = {
			gestureHandling: this.map.get('gestureHandling'),
			draggable: this.map.get('draggable'),
			scrollwheel: this.map.get('scrollwheel'),
		};
		this.map.setOptions({ gestureHandling: 'none', draggable: false, scrollwheel: false });
		canvas.addEventListener('pointerdown', this.lassoDown);
		canvas.addEventListener('pointermove', this.lassoMove);
		canvas.addEventListener('pointerup', this.lassoUp);
		canvas.addEventListener('pointercancel', this.lassoUp);
		canvas.addEventListener('touchstart', this.blockTouch, { passive: false });
		canvas.addEventListener('touchmove', this.blockTouch, { passive: false });
		this.drawHint = remove ? 'Draw around prospects to unselect' : 'Draw around prospects to select';
		this.mapStatus = '';
	}

	private exitLasso(): void {
		this.lassoMode = 'none';
		if (!this.lassoActive) { return; }
		this.lassoActive = false;
		this.lassoDrawing = false;
		this.lassoPts = [];
		this.lassoPointerId = null;
		this.drawHint = '';
		const canvas = this.lassoCanvas?.nativeElement;
		if (canvas) {
			canvas.setAttribute('hidden', '');
			canvas.classList.remove('is-active');
			canvas.removeEventListener('pointerdown', this.lassoDown);
			canvas.removeEventListener('pointermove', this.lassoMove);
			canvas.removeEventListener('pointerup', this.lassoUp);
			canvas.removeEventListener('pointercancel', this.lassoUp);
			canvas.removeEventListener('touchstart', this.blockTouch);
			canvas.removeEventListener('touchmove', this.blockTouch);
			const path = canvas.querySelector('.bp-lasso-path') as SVGPathElement | null;
			if (path) { path.setAttribute('d', ''); }
		}
		if (this.map && this.lassoSavedGest) {
			this.map.setOptions(this.lassoSavedGest);
			this.lassoSavedGest = null;
		}
	}

	private onLassoDown(e: PointerEvent): void {
		if (this.lassoPointerId !== null) { return; }
		const canvas = this.lassoCanvas.nativeElement;
		try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
		this.lassoPointerId = e.pointerId;
		this.lassoDrawing = true;
		const rect = canvas.getBoundingClientRect();
		this.lassoPts = [{ x: e.clientX - rect.left, y: e.clientY - rect.top }];
		this.renderLasso();
		e.preventDefault();
	}

	private onLassoMove(e: PointerEvent): void {
		if (!this.lassoDrawing || e.pointerId !== this.lassoPointerId) { return; }
		const canvas = this.lassoCanvas.nativeElement;
		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;
		const last = this.lassoPts[this.lassoPts.length - 1];
		if (last && Math.abs(x - last.x) < 2 && Math.abs(y - last.y) < 2) { return; }
		this.lassoPts.push({ x, y });
		this.renderLasso();
		e.preventDefault();
	}

	private onLassoUp(e: PointerEvent): void {
		if (e.pointerId !== this.lassoPointerId) { return; }
		this.lassoDrawing = false;
		this.lassoPointerId = null;
		try { this.lassoCanvas.nativeElement.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
		if (this.lassoPts.length < 3) {
			this.exitLasso();
			return;
		}
		this.commitLasso();
	}

	private renderLasso(): void {
		const path = this.lassoCanvas.nativeElement.querySelector('.bp-lasso-path') as SVGPathElement | null;
		if (!path || !this.lassoPts.length) {
			if (path) { path.setAttribute('d', ''); }
			return;
		}
		let d = 'M' + this.lassoPts[0].x + ' ' + this.lassoPts[0].y;
		for (let i = 1; i < this.lassoPts.length; i++) {
			d += ' L' + this.lassoPts[i].x + ' ' + this.lassoPts[i].y;
		}
		path.setAttribute('d', this.lassoDrawing ? d : d + ' Z');
	}

	private commitLasso(): void {
		const pixelPts = this.lassoPts.slice();
		const remove = this.lassoRemove;
		let hit = 0;
		this.mapData.forEach((r) => {
			if (!r.prospect_key || !this.validCoord(r.lat, r.lng)) { return; }
			const px = this.latLngToPixel(Number(r.lat), Number(r.lng));
			if (!px || !pointInPixelPoly(px.x, px.y, pixelPts)) { return; }
			hit++;
			if (remove) {
				this.selectedKeys.delete(String(r.prospect_key));
			} else {
				this.selectedKeys.add(String(r.prospect_key));
			}
		});
		this.exitLasso();
		this.mapStatus = hit
			? (this.selectedKeys.size + ' selected')
			: 'No prospects inside that area';
		this.schedulePaint();
	}

	private latLngToPixel(lat: number, lng: number): { x: number; y: number } | null {
		const projection = this.map.getProjection();
		if (!projection) { return null; }
		const bounds = this.map.getBounds();
		if (!bounds) { return null; }
		const nw = projection.fromLatLngToPoint(bounds.getNorthEast());
		const se = projection.fromLatLngToPoint(bounds.getSouthWest());
		const worldPoint = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
		const mapW = this.mapHost.nativeElement.offsetWidth;
		const mapH = this.mapHost.nativeElement.offsetHeight;
		return {
			x: ((worldPoint.x - se.x) / (nw.x - se.x)) * mapW,
			y: ((worldPoint.y - nw.y) / (se.y - nw.y)) * mapH,
		};
	}

	clearSelection(): void {
		this.selectedKeys.clear();
		this.mapStatus = '';
		this.schedulePaint();
	}

	previewRowOnMap(row: any): void {
		if (this.infoPinned) { return; }
		this.activeRowKey = this.markerKey(row);
		this.openInfoForRow(row, false, false);
	}

	clearRowPreview(row: any): void {
		if (this.infoPinned || this.activeRowKey !== this.markerKey(row)) { return; }
		this.activeRowKey = '';
		if (this.infoWindow) { this.infoWindow.close(); }
	}

	focusRowOnMap(row: any): void {
		this.activeRowKey = this.markerKey(row);
		this.openInfoForRow(row, true, true);
	}

	onTableRowKeydown(event: KeyboardEvent, row: any): void {
		if (event.key !== 'Enter' && event.key !== ' ') { return; }
		event.preventDefault();
		this.focusRowOnMap(row);
	}

	assignSelected(): void {
		if (!this.scratchEnabled || !this.selectedKeys.size || !this.assignRep) {
			return;
		}
		this.generalLedgerService.saveBeauticianProspectAssignments({
			prospect_keys: Array.from(this.selectedKeys),
			assigned_rep: this.assignRep,
		}).subscribe(() => {
			this.selectedKeys.clear();
			this.runReport();
		});
	}

	clearSelectedAssignments(): void {
		if (!this.scratchEnabled || !this.selectedKeys.size) {
			return;
		}
		this.generalLedgerService.saveBeauticianProspectAssignments({
			prospect_keys: Array.from(this.selectedKeys),
			assigned_rep: '',
		}).subscribe(() => {
			this.selectedKeys.clear();
			this.runReport();
		});
	}

	isRepSelected(rep: string): boolean {
		const selected = this.formGroup.get('reps')?.value || [];
		return selected.length === 1 && selected[0] === rep;
	}

	clearRepFilter(): void {
		this.formGroup.get('reps')?.setValue([]);
	}

	selectRep(rep: string): void {
		this.formGroup.get('reps')?.setValue(this.isRepSelected(rep) ? [] : [rep]);
	}

	private markerColor(r: any): string {
		if (r.prospect_key && this.selectedKeys.has(String(r.prospect_key))) {
			return '#b5541e';
		}
		if (r.matched_debtorno && r.assignment_rep) {
			return '#2e7d32';
		}
		if (r.assignment_rep) {
			return '#8e24aa';
		}
		return this.isProspect ? '#1e88e5' : '#2e7d32';
	}

	private paintMapClusters(rows: any[]): void {
		const projection = this.map.getProjection();
		if (!projection) {
			return;
		}
		const scale = Math.pow(2, this.map.getZoom() || 8);
		const cells = new Map<string, any[]>();
		const selectedRows: any[] = [];

		rows.forEach((r) => {
			if (r.prospect_key && this.selectedKeys.has(String(r.prospect_key))) {
				selectedRows.push(r);
				return;
			}
			const point = projection.fromLatLngToPoint(new google.maps.LatLng(Number(r.lat), Number(r.lng)));
			const key = Math.floor((point.x * scale) / this.clusterCellPx) + ':' + Math.floor((point.y * scale) / this.clusterCellPx);
			const bucket = cells.get(key);
			if (bucket) {
				bucket.push(r);
			} else {
				cells.set(key, [r]);
			}
		});

		const singles = selectedRows.slice();
		cells.forEach(bucket => {
			if (bucket.length === 1) {
				singles.push(bucket[0]);
			}
		});

		singles.forEach(r => this.addMapMarker(r));

		cells.forEach(bucket => {
			if (bucket.length < 2) {
				return;
			}
			let lat = 0;
			let lng = 0;
			bucket.forEach(r => {
				lat += Number(r.lat);
				lng += Number(r.lng);
			});
			lat = lat / bucket.length;
			lng = lng / bucket.length;
			const count = bucket.length;
			const marker = new google.maps.Marker({
				position: { lat, lng },
				map: this.map,
				title: count + ' prospects - click to zoom',
				optimized: true,
				icon: {
					path: google.maps.SymbolPath.CIRCLE,
					fillColor: '#b5541e',
					fillOpacity: 0.9,
					strokeColor: '#ffffff',
					strokeWeight: 2,
					scale: count >= 100 ? 20 : count >= 25 ? 16 : 13,
				},
				label: {
					text: String(count),
					color: '#ffffff',
					fontSize: '11px',
					fontWeight: '700',
				},
			});
			marker.addListener('click', () => {
				const bounds = new google.maps.LatLngBounds();
				bucket.forEach(r => bounds.extend({ lat: Number(r.lat), lng: Number(r.lng) }));
				this.map.fitBounds(bounds, 80);
			});
			this.clusterMarkers.push(marker);
		});
	}

	private addMapMarker(r: any): void {
		const marker = new google.maps.Marker({
			position: { lat: Number(r.lat), lng: Number(r.lng) },
			map: this.map,
			title: r.business_name || r.address || '',
			optimized: true,
			icon: this.markerIcon(r),
		});
		marker.set('bpRow', r);
		this.bindMarkerInfo(marker);
		this.markerByKey.set(this.markerKey(r), marker);
	}

	// Hover opens the detail bubble (address, owner, estimate, twin); click pins
	// it open so it survives moving the mouse away.
	private bindMarkerInfo(marker: any): void {
		marker.addListener('mouseover', () => { if (!this.infoPinned) { this.openInfo(marker); } });
		marker.addListener('mouseout', () => { if (!this.infoPinned) { this.infoWindow.close(); } });
		marker.addListener('click', () => { this.infoPinned = true; this.openInfo(marker); });
	}

	private openInfo(marker: any): void {
		if (!this.infoWindow) { return; }
		this.infoWindow.setContent(this.mapInfo(marker.get('bpRow')));
		this.infoWindow.open(this.map, marker);
	}

	private openInfoForRow(row: any, pinned: boolean, pan: boolean): void {
		const mapRow = this.findMapRow(row);
		if (!mapRow || !this.map || !this.infoWindow || !this.validCoord(mapRow.lat, mapRow.lng)) {
			return;
		}
		this.infoPinned = pinned;
		const key = this.markerKey(mapRow);
		const position = { lat: Number(mapRow.lat), lng: Number(mapRow.lng) };
		if (pan) {
			this.map.panTo(position);
			if ((this.map.getZoom() || 0) < 14) {
				this.map.setZoom(14);
			}
			this.schedulePaint(true);
		}
		const open = () => {
			const marker = this.markerByKey.get(key);
			if (marker) {
				this.openInfo(marker);
				return;
			}
			this.infoWindow.setContent(this.mapInfo(mapRow));
			this.infoWindow.setPosition(position);
			this.infoWindow.open(this.map);
		};
		if (pan) {
			setTimeout(open, 80);
		} else {
			open();
		}
	}

	private markerKey(r: any): string {
		return String(r.prospect_key || r.license_number || '');
	}

	private findMapRow(row: any): any {
		const key = this.markerKey(row);
		if (!key) { return null; }
		return this.mapData.find(r => this.markerKey(r) === key) || row;
	}

	private fitMapToRows(rows: any[], bounds?: any): void {
		if (!this.map || this.hasAutoFit || !rows.length) {
			return;
		}
		const b = bounds || new google.maps.LatLngBounds();
		if (!bounds) {
			rows.forEach(r => b.extend({ lat: Number(r.lat), lng: Number(r.lng) }));
		}
		google.maps.event.trigger(this.map, 'resize');
		if (rows.length === 1) {
			this.map.setCenter(b.getCenter());
			this.map.setZoom(13);
		} else {
			this.map.fitBounds(b, 60);
			google.maps.event.addListenerOnce(this.map, 'idle', () => {
				const z = this.map.getZoom() || 0;
				if (z > 13) {
					this.map.setZoom(13);
				}
			});
		}
		this.hasAutoFit = true;
	}

	private validCoord(lat: any, lng: any): boolean {
		const la = Number(lat);
		const ln = Number(lng);
		return Number.isFinite(la) && Number.isFinite(ln) && la !== 0 && ln !== 0;
	}

	private mapInfo(r: any): string {
		const owner = this.isProspect ? (r.rep || 'UNASSIGNED') : (r.matched_rep || '');
		const address = [r.address, r.suite, r.city, r.zip5].filter(Boolean).join(', ');
		let est = '';
		if (this.isProspect && Number(r.est_potential) > 0) {
			est = '<div style="margin-top:4px;font-weight:700;color:#1e88e5">Est. ~$' + this.fmtMoney(r.est_potential) + '/yr</div>';
			const comps = Number(r.est_comps) || 0;
			if (comps) {
				est += '<div style="color:#94a3b8;font-size:11px">median of ' + comps + ' comparable customers your size</div>';
			}
			if (r.est_twin) {
				est += '<div style="color:#64748b;font-size:12px">Looks like <strong>' + this.escapeHtml(r.est_twin) + '</strong>'
					+ (Number(r.est_twin_ttm) > 0 ? ' — $' + this.fmtMoney(r.est_twin_ttm) + '/yr' : '') + '</div>';
			}
		}
		return '<div class="bp-map-info">'
			+ '<strong>' + this.escapeHtml(r.business_name || '') + '</strong>'
			+ '<div>' + this.escapeHtml(address) + '</div>'
			+ '<div>' + this.escapeHtml(owner) + '</div>'
			+ (r.assignment_rep ? '<div>Assigned: ' + this.escapeHtml(r.assignment_rep) + '</div>' : '')
			+ est
			+ '</div>';
	}

	private fmtMoney(v: any): string {
		return Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
	}

	private escapeHtml(value: string): string {
		return String(value).replace(/[&<>"']/g, ch => ({
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#39;',
		}[ch]));
	}
}
